import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { trackIntegrationEvent } from '@modules/integrationsV2/utils';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  DialogActions,
  DialogContent,
  TextField,
  TextFieldProps,
} from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
  IntegrationInputData,
  IntegrationType,
} from 'generated/graphql';
import { ChangeEvent } from 'react';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';
import { toTitleCase } from 'v2/utils/strings';
import { useMuiModalContext } from '../Modal/ModalContext';

interface SingleInputConnectIntegrationFormModalProps {
  channelType: IntegrationChannelType;
  integrationType: IntegrationType;
  inputFieldData: string;
  setInputFieldData: (value: string) => void;
  integrationData: IntegrationInputData;
  textFieldProps?: TextFieldProps;
  modalTitle?: string;
  buttonText?: string;
}

export const SingleInputConnectIntegrationFormModal = ({
  channelType,
  inputFieldData,
  setInputFieldData,
  integrationData,
  integrationType,
  textFieldProps = {},
  modalTitle = channelType,
  buttonText = 'Connect',
}: SingleInputConnectIntegrationFormModalProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { closeModal } = useMuiModalContext();

  const { status, createIntegration } = useCreateIntegrationEdgeServer({
    tenantID,
    direction: IntegrationDirection.Inbound,
    channel: channelType,
    integrationType,
    createdByUser: userID,
    inputData: integrationData,
    onCompleted: () => {
      closeModal();
      trackIntegrationEvent({
        eventName: 'Integration Added',
        integrationChannelType: channelType,
      });
    },
  });

  const handleSubmit = () => {
    createIntegration();
    trackIntegrationEvent({
      eventName: 'Integration Form Connect CTA Clicked',
      integrationChannelType: channelType,
    });
  };

  const loading = IsLoading(status);

  const handleTextInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputFieldData(event.target.value);
  };

  const title = `${modalTitle} - ${toTitleCase(integrationType)} Integration`;

  return (
    <>
      <ModalHeader title={title} showCloseButton={true} />
      <DialogContent dividers>
        <Box pt={1}>
          <TextField
            {...textFieldProps}
            fullWidth
            variant="outlined"
            onChange={handleTextInputChange}
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <LoadingButton
          variant="contained"
          type="submit"
          size="large"
          fullWidth
          onClick={handleSubmit}
          loading={loading}
          disabled={!inputFieldData.length}
        >
          {buttonText}
        </LoadingButton>
      </DialogActions>
    </>
  );
};
